/**
 * Typed wrappers around the Phase A gamification endpoints.
 *
 * Backend reference: ``backend/caitapi/routers/gamification.py``. Types
 * mirror the Pydantic response models 1:1 — keep in sync when those
 * change.
 *
 * Auth: all endpoints require an authenticated session (401) plus an
 * active enrollment (403). Callers handle 401 by redirecting through
 * ``/api/auth/login?return_to=...`` (same pattern as MyJournalPage).
 *
 * Phase B will add the streak-write side; this surface is read/mark
 * only.
 */
import { apiGet, apiPost } from "./api";

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export type BadgeTier = "bronze" | "silver" | "gold";

export interface BadgeStatus {
  slug: string;
  title: string;
  /** Learner-facing criterion line. Always shown, earned or not. */
  description: string;
  /** lucide-react icon name (in-bundle SVG; no CSP img-src change). */
  icon: string;
  tier: BadgeTier;
  earned: boolean;
  /** ISO 8601 timestamp the badge was awarded, or null when locked. */
  awarded_at: string | null;
  /** True iff earned AND the learner has dismissed the toast or visited the shelf. */
  seen: boolean;
}

export interface BadgesResponse {
  badges: BadgeStatus[];
  earned_count: number;
  total_count: number;
}

export function getBadges(): Promise<BadgesResponse> {
  return apiGet<BadgesResponse>("/api/gamification/badges");
}

// ---------------------------------------------------------------------------
// XP
// ---------------------------------------------------------------------------

export interface XpResponse {
  total_xp: number;
  /** Zero-filled keys: lesson | module_quiz | exam | capstone | streak. */
  by_source: Record<string, number>;
}

export function getXp(): Promise<XpResponse> {
  return apiGet<XpResponse>("/api/gamification/xp");
}

// ---------------------------------------------------------------------------
// Streak (Phase A always returns the zero object; Phase B starts writing)
// ---------------------------------------------------------------------------

export interface StreakResponse {
  current_len: number;
  longest_len: number;
  freeze_tokens: number;
  /** Calendar day in the course timezone (America/New_York). ISO date. */
  last_active_date: string | null;
}

export function getStreak(): Promise<StreakResponse> {
  return apiGet<StreakResponse>("/api/gamification/streak");
}

// ---------------------------------------------------------------------------
// Mark badges seen (clears the toast)
// ---------------------------------------------------------------------------

export interface MarkSeenResponse {
  marked: number;
}

/**
 * Mark unseen badges as seen.
 *
 * - Omit ``slugs`` to mark every unseen badge as seen (the "I just viewed
 *   the shelf" case).
 * - Pass a list to scope the mark to the toast'd badges (the "I just
 *   dismissed the toast for these specific badges" case).
 *
 * Idempotent — already-seen rows are excluded server-side by the
 * ``seen_at IS NULL`` predicate.
 */
export function markBadgesSeen(slugs?: string[]): Promise<MarkSeenResponse> {
  return apiPost<MarkSeenResponse>(
    "/api/gamification/badges/seen",
    slugs && slugs.length > 0 ? { slugs } : undefined,
  );
}
